import React, { useRef, useState } from 'react';
import { BORDER, DocTitle, SectionBar } from './ui';

const MAX_MB = 10;
const ACCEPT = '.pdf,image/*';

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const UploadIcon = () => (
  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 16V4m0 0l-4 4m4-4l4 4M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3" />
  </svg>
);

const FileRow = ({ file, onRemove }) => (
  <li className="flex items-center justify-between gap-3 rounded border border-gray-200 bg-white px-3 py-2 text-sm">
    <span className="flex min-w-0 items-center gap-2">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-green-100 text-green-700">
        <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 10.5l3.5 3.5 7.5-8" /></svg>
      </span>
      <span className="truncate font-medium text-gray-800">{file.name}</span>
      <span className="shrink-0 text-xs text-gray-400">{fmtSize(file.size)}</span>
    </span>
    <button type="button" onClick={onRemove} className="shrink-0 text-xs font-semibold text-red-600 hover:underline">Remove</button>
  </li>
);

const DropCard = ({ title, hint, multiple, files, onAdd, onRemove, error }) => {
  const input = useRef(null);
  const [drag, setDrag] = useState(false);
  const [sizeErr, setSizeErr] = useState('');

  const take = (list) => {
    const all = Array.from(list || []);
    const ok = all.filter((f) => f.size <= MAX_MB * 1048576);
    setSizeErr(ok.length < all.length ? `Files must be ${MAX_MB} MB or smaller.` : '');
    if (ok.length) onAdd(multiple ? ok : [ok[0]]);
  };

  const shownError = sizeErr || error;

  return (
    <div className={`border ${BORDER} ${shownError ? 'border-red-400' : ''}`}>
      <div className="border-b border-[#cfcac2] bg-[#f4f2ee] px-4 py-2.5">
        <h4 className="text-[13px] font-bold text-gray-900">{title} <span className="text-red-600">*</span></h4>
        <p className="text-xs text-gray-500">{hint}</p>
      </div>
      <div className="space-y-3 p-4">
        <div
          role="button"
          tabIndex={0}
          onClick={() => input.current?.click()}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), input.current?.click())}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); take(e.dataTransfer.files); }}
          className={`flex cursor-pointer flex-col items-center gap-1 rounded border-2 border-dashed px-4 py-6 text-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
            drag ? 'border-blue-600 bg-blue-50' : 'border-gray-300 bg-[#faf9f7] hover:border-blue-500 hover:bg-blue-50/40'
          }`}
        >
          <span className="text-blue-700"><UploadIcon /></span>
          <span className="text-sm font-semibold text-gray-800">Click to upload <span className="font-normal text-gray-500">or drag and drop</span></span>
          <span className="text-xs text-gray-400">PDF, JPG or PNG · up to {MAX_MB} MB{multiple ? ' · multiple files allowed' : ''}</span>
          <input
            ref={input}
            type="file"
            accept={ACCEPT}
            multiple={multiple}
            className="hidden"
            onChange={(e) => { take(e.target.files); e.target.value = ''; }}
          />
        </div>
        {shownError && <p role="alert" className="text-xs text-red-600">{shownError}</p>}
        {files.length > 0 && (
          <ul className="space-y-2">
            {files.map((f, i) => <FileRow key={`${f.name}-${i}`} file={f} onRemove={() => onRemove(i)} />)}
          </ul>
        )}
      </div>
    </div>
  );
};

const StepDocuments = ({ files, setFiles, showErrors }) => (
  <div className="space-y-6">
    <DocTitle subtitle="Upload the supporting documents we need to set up your payroll account.">Supporting Documents</DocTitle>
    <SectionBar>Documents</SectionBar>

    <DropCard
      title="Government-issued ID for each owner listed"
      hint="Driver’s license or passport for everyone listed in the Ownership section."
      multiple
      files={files.ownerIds}
      onAdd={(list) => setFiles((p) => ({ ...p, ownerIds: [...p.ownerIds, ...list] }))}
      onRemove={(i) => setFiles((p) => ({ ...p, ownerIds: p.ownerIds.filter((_, k) => k !== i) }))}
      error={showErrors && files.ownerIds.length === 0 ? 'Upload at least one owner ID.' : ''}
    />

    <DropCard
      title="IRS EIN Letter"
      hint="The official IRS CP 575 or 147C letter."
      files={files.einLetter ? [files.einLetter] : []}
      onAdd={([f]) => setFiles((p) => ({ ...p, einLetter: f }))}
      onRemove={() => setFiles((p) => ({ ...p, einLetter: null }))}
      error={showErrors && !files.einLetter ? 'The EIN letter is required.' : ''}
    />

    <DropCard
      title="Signed client / customer contract"
      hint="A copy of your signed contract – required for payroll account setup."
      files={files.signedContract ? [files.signedContract] : []}
      onAdd={([f]) => setFiles((p) => ({ ...p, signedContract: f }))}
      onRemove={() => setFiles((p) => ({ ...p, signedContract: null }))}
      error={showErrors && !files.signedContract ? 'A signed client contract is required.' : ''}
    />
  </div>
);

export default StepDocuments;
